import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import GroupsIcon from '@mui/icons-material/Groups';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EventIcon from '@mui/icons-material/Event';

import StatusChip, { subscriptionStatusMap, getSubscriptionDisplayStatus } from '../common/feedback/StatusChip';
import { Subscription } from '../../types';
import { formatDate } from '../../utils/date-formatter';
import { isExpired, isExpiringSoon } from '../../utils/subscription-helpers';

// Row Actions Cell Component for managing per-row menu
function RowActionsCell({
  subscription,
  onCancel,
  onReactivate,
  onDelete,
  onAssignTeams,
  onManageAddOns,
  onEditReferenceName,
  showEditButton = true,
  showManageAddOnsButton = true,
  showCancelButton = true,
  showDeleteButton = true,
  showAssignTeamsButton = true
}: {
  subscription: Subscription;
  onCancel: (subscription: Subscription) => void;
  onReactivate: (subscription: Subscription) => void;
  onDelete: (subscription: Subscription) => void;
  onAssignTeams: (subscription: Subscription) => void;
  onManageAddOns: (subscription: Subscription) => void;
  onEditReferenceName: (subscription: Subscription) => void;
  showEditButton?: boolean;
  showManageAddOnsButton?: boolean;
  showCancelButton?: boolean;
  showDeleteButton?: boolean;
  showAssignTeamsButton?: boolean;
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (action: 'cancel' | 'reactivate' | 'delete' | 'assignTeams' | 'manageAddOns' | 'editReferenceName') => {
    switch (action) {
      case 'cancel':
        onCancel(subscription);
        break;
      case 'reactivate':
        onReactivate(subscription);
        break;
      case 'delete':
        onDelete(subscription);
        break;
      case 'assignTeams':
        onAssignTeams(subscription);
        break;
      case 'manageAddOns':
        onManageAddOns(subscription);
        break;
      case 'editReferenceName':
        onEditReferenceName(subscription);
        break;
    }
    handleClose();
  };

  // Check if subscription is pending cancellation (active but scheduled to cancel at period end)
  // This includes:
  // 1. Status is 'active' but autoRenew is false (user cancelled but subscription still active until period end)
  // 2. Status is 'cancelled' but not yet expired (legacy state, kept for backward compatibility)
  const isPendingCancellation =
    (subscription.status === 'active' && subscription.autoRenew === false && !isExpired(subscription)) ||
    (subscription.status === 'cancelled' &&
     subscription.autoRenew === false &&
     !isExpired(subscription));

  // Check if subscription is fully expired
  const expired = isExpired(subscription);

  // Debug logging for action menu visibility
  console.log('🔍 RowActionsCell debug:', {
    subscriptionId: subscription.id || subscription._id,
    subscriptionName: subscription.name,
    isPendingCancellation,
    expired,
    showCancelButton,
    showDeleteButton,
    status: subscription.status,
    autoRenew: subscription.autoRenew,
    currentPeriodEnd: subscription.currentPeriodEnd,
    endDate: subscription.endDate
  });

  return (
    <Box>
      <IconButton
        aria-label="actions"
        aria-controls={`actions-menu-${subscription.id || subscription._id}`}
        aria-haspopup="true"
        onClick={handleClick}
        size="small"
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      
      {/* Actions Menu */}
      <Menu
        id={`actions-menu-${subscription.id || subscription._id}`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {showEditButton && (
          <MenuItem onClick={() => handleAction('editReferenceName')}>
            <ListItemIcon>
              <DriveFileRenameOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Name</ListItemText>
          </MenuItem>
        )}

        {showAssignTeamsButton && (
          <MenuItem onClick={() => handleAction('assignTeams')}>
            <ListItemIcon>
              <GroupsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Manage Teams</ListItemText>
          </MenuItem>
        )}

        {/* Hide Manage Add-ons for subscriptions pending cancellation, expired, or without permission */}
        {showManageAddOnsButton && !isPendingCancellation && !expired && (
          <MenuItem onClick={() => handleAction('manageAddOns')}>
            <ListItemIcon>
              <AddCircleOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Manage Add-ons</ListItemText>
          </MenuItem>
        )}

        {/* Show Cancel option only for active subscriptions with auto-renew enabled and permission */}
        {showCancelButton && subscription.status === 'active' && subscription.autoRenew !== false && (
          <MenuItem
            onClick={() => handleAction('cancel')}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <CancelIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Cancel Subscription</ListItemText>
          </MenuItem>
        )}

        {/* Show Reactivate option only for subscriptions pending cancellation (not yet expired) */}
        {showCancelButton && isPendingCancellation && !expired && (
          <MenuItem
            onClick={() => handleAction('reactivate')}
            sx={{ color: 'success.main' }}
          >
            <ListItemIcon>
              <RestoreIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText>Reactivate Subscription</ListItemText>
          </MenuItem>
        )}

        {/* Show Delete option only for expired subscriptions and with permission */}
        {showDeleteButton && expired && (
          <MenuItem
            onClick={() => handleAction('delete')}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <DeleteForeverIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete Subscription</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}

interface SubscriptionTableProps {
  subscriptions: Subscription[];
  isLoading: boolean;
  selectedSubscriptionId?: string | null;
  onRowClick: (subscription: Subscription) => void;
  onCancel: (subscription: Subscription) => void;
  onReactivate: (subscription: Subscription) => void;
  onDelete: (subscription: Subscription) => void;
  onAssignTeams: (subscription: Subscription) => void;
  onManageAddOns: (subscription: Subscription) => void;
  onEditReferenceName: (subscription: Subscription) => void;
  onCreateSubscription: () => void;
  // Permission flags for action visibility
  showEditButton?: boolean;
  showManageAddOnsButton?: boolean;
  showCancelButton?: boolean;
  showDeleteButton?: boolean;
  showAssignTeamsButton?: boolean;
  showCreateButton?: boolean;
}

const SubscriptionTable: React.FC<SubscriptionTableProps> = ({
  subscriptions,
  isLoading,
  selectedSubscriptionId,
  onRowClick,
  onCancel,
  onReactivate,
  onDelete,
  onAssignTeams,
  onManageAddOns,
  onEditReferenceName,
  onCreateSubscription,
  showEditButton = true,
  showManageAddOnsButton = true,
  showCancelButton = true,
  showDeleteButton = true,
  showAssignTeamsButton = true,
  showCreateButton = true
}) => {
  // Debug logging to check data structure
  React.useEffect(() => {
    if (subscriptions.length > 0) {
      console.log('🔍 SubscriptionTable - subscriptions data:', subscriptions);
      console.log('🔍 First subscription details:', {
        id: subscriptions[0].id,
        _id: subscriptions[0]._id,
        organizationId: subscriptions[0].organizationId, // CRITICAL: Check organizationId
        currentUsage: subscriptions[0].currentUsage,
        limits: subscriptions[0].limits,
        assignedTeamCount: subscriptions[0].assignedTeamCount
      });
      console.log('🔍 All subscription organizationIds:', subscriptions.map(s => ({
        id: s.id || s._id,
        name: s.name,
        organizationId: s.organizationId
      })));
    }
  }, [subscriptions]);
  // Define columns for DataGrid
  const columns: GridColDef<Subscription>[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'plan',
      headerName: 'Plan',
      width: 120,
      renderCell: (params: GridRenderCellParams<Subscription>) => {
        // API already provides formatted plan names (e.g., "Free", "Standard", "Plus")
        return params.row.plan || params.row.planId || 'Unknown';
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 280,
      renderCell: (params: GridRenderCellParams<Subscription>) => {
        const subscription = params.row;

        // Get effective display status based on availability and status
        const displayStatus = getSubscriptionDisplayStatus(subscription.status, subscription.availability);

        // Check if subscription is pending cancellation (active but autoRenew is false)
        // This matches the logic in RowActionsCell for consistency
        const isPendingCancellation =
          (subscription.status === 'active' && subscription.autoRenew === false && !isExpired(subscription)) ||
          (subscription.status === 'cancelled' &&
           subscription.autoRenew === false &&
           !isExpired(subscription));

        return (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <StatusChip status={displayStatus} statusMap={subscriptionStatusMap} />
            {isPendingCancellation && (
              <Chip
                label="Canceled"
                color="error"
                size="small"
                icon={<CancelIcon />}
                sx={{ fontWeight: 500 }}
              />
            )}
          </Box>
        );
      }
    },
    {
      field: 'teams',
      headerName: 'Teams',
      width: 120,
      renderCell: (params: GridRenderCellParams<Subscription>) => {
        const row = params.row;
        
        // Debug logging
        console.log('Teams column data:', {
          assignedTeamCount: row.assignedTeamCount,
          currentUsage: row.currentUsage,
          limits: row.limits
        });
        
        // Get teams used - prioritize currentUsage.teamsUsed, then assignedTeamCount
        const teamsUsed = row.currentUsage?.teamsUsed ?? row.assignedTeamCount ?? row.teams?.length ?? 0;
        
        // Get max teams from limits
        const maxTeams = (row.limits?.maxTeams ?? row.limits?.teamsPerOrg ?? 0) + (row.addOns?.extraTeams ?? 0);
        
        return `${teamsUsed} / ${maxTeams}`;
      }
    },
    {
      field: 'storage',
      headerName: 'Storage',
      width: 140,
      renderCell: (params: GridRenderCellParams<Subscription>) => {
        const row = params.row;
        
        // Debug logging
        console.log('Storage column data:', {
          currentUsage: row.currentUsage,
          limits: row.limits
        });
        
        const storageUsed = row.currentUsage?.storageUsedBytes ?? row.usage?.storageUsedBytes ?? 0;
        const maxStorage = row.limits?.maxStorageBytes ?? row.limits?.storage ?? 0;
        
        // Convert bytes to human-readable format
        const formatBytes = (bytes: number) => {
          if (bytes === 0) return '0 B';
          const k = 1024;
          const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
          const i = Math.floor(Math.log(bytes) / Math.log(k));
          return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
        };
        
        return `${formatBytes(storageUsed)} / ${formatBytes(maxStorage)}`;
      }
    },
    {
      field: 'endDate',
      headerName: 'Next Billing/Expiration',
      width: 180,
      renderCell: (params: GridRenderCellParams<Subscription>) => {
        const subscription = params.row;

        if (subscription.status === 'cancelled') {
          return 'Canceled';
        }

        const expired = isExpired(subscription);
        const expiringSoon = isExpiringSoon(subscription);
        const willRenew = subscription.autoRenew !== false && !expired;

        // Use currentPeriodEnd from API or fall back to endDate
        const date = subscription.currentPeriodEnd || subscription.endDate;
        const formattedDate = date ? formatDate(date) : '-';

        // Apply color based on expiration status
        let color = 'inherit';
        if (expired) {
          color = 'error.main'; // Red for expired
        } else if (expiringSoon && !willRenew) {
          color = 'warning.main'; // Orange for expiring soon (but only if not renewing)
        }

        // Check if date is in the past
        const isPastDate = date && new Date(date) < new Date();

        // Show appropriate prefix based on auto-renew status and date
        const prefix = willRenew ? 'Renews: ' : isPastDate ? 'Expired: ' : 'Expires: ';

        return (
          <Box component="span" sx={{ color, fontWeight: (expired || (expiringSoon && !willRenew)) ? 600 : 400 }}>
            {prefix}{formattedDate}
          </Box>
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      width: 80,
      align: 'right',
      renderCell: (params: GridRenderCellParams<Subscription>) => (
        <RowActionsCell
          subscription={params.row}
          onCancel={onCancel}
          onReactivate={onReactivate}
          onDelete={onDelete}
          onAssignTeams={onAssignTeams}
          onManageAddOns={onManageAddOns}
          onEditReferenceName={onEditReferenceName}
          showEditButton={showEditButton}
          showManageAddOnsButton={showManageAddOnsButton}
          showCancelButton={showCancelButton}
          showDeleteButton={showDeleteButton}
          showAssignTeamsButton={showAssignTeamsButton}
        />
      )
    }
  ];

  return (
    <Box>
      {/* Actions Button */}
      {showCreateButton && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={onCreateSubscription}
          >
            Add Subscription
          </Button>
        </Box>
      )}

      <DataGrid
        rows={subscriptions}
        columns={columns}
        loading={isLoading}
        getRowId={(row: Subscription) => row.id || row._id}
        autoHeight
        onRowClick={(params) => onRowClick(params.row)}
        sx={{
          '& .MuiDataGrid-columnHeaders': {
            overflow: 'visible',
          },
          '& .MuiDataGrid-cell': {
            overflow: 'visible',
          },
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
          '& .MuiDataGrid-row.Mui-selected': {
            backgroundColor: 'action.selected',
            '&:hover': {
              backgroundColor: 'action.selected',
            },
          },
          '& .MuiDataGrid-row.subscription-expired': {
            backgroundColor: 'error.lighter',
            '&:hover': {
              backgroundColor: 'error.light',
            },
          },
          '& .MuiDataGrid-row.subscription-expiring': {
            backgroundColor: 'warning.lighter',
            '&:hover': {
              backgroundColor: 'warning.light',
            },
          },
        }}
        getRowClassName={(params) => {
          const classes = [];

          // Add selected class if this row is selected
          if (params.row.id === selectedSubscriptionId || params.row._id === selectedSubscriptionId) {
            classes.push('Mui-selected');
          }

          // Add expiration status classes
          if (isExpired(params.row)) {
            classes.push('subscription-expired');
          } else if (isExpiringSoon(params.row)) {
            classes.push('subscription-expiring');
          }

          return classes.join(' ');
        }}
        density="standard"
        pageSizeOptions={[5, 10, 25]}
        initialState={{
          pagination: { paginationModel: { pageSize: 5 } },
        }}
        localeText={{
          noRowsLabel: 'No subscriptions active',
          noResultsOverlayLabel: 'No subscriptions found',
          MuiTablePagination: {
            labelRowsPerPage: 'Subscriptions per page',
          },
        }}
      />
    </Box>
  );
};

export default SubscriptionTable;